import type { BrokerProduct } from '@/services/brokers/types';
import { useRequirePermissions } from '@/shared/hooks';
import { ArrowDownUp, ChevronDown, ChevronUp, Edit } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useBrokerDetails, useBrokerProducts } from '../hooks';
import { brokerActionBlockedReason } from '../utils/broker-status.util';
import { groupProductsByGroup } from '../utils/product-groups.util';
import { BrokerProductsModal } from './broker-products-modal.component';

interface BrokerProductsListProps {
  brokerId: string;
}

type SortColumn = 'name' | 'code' | 'status';
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn; direction: SortDirection } | null;

export function BrokerProductsList({ brokerId }: BrokerProductsListProps) {
  const { data, isLoading, isError, error } = useBrokerProducts(brokerId);
  const { hasPermission: canManageProducts } = useRequirePermissions({
    permissions: ['parties.brokers.products.write'],
  });

  // A Api recusa ativar produto em corretora inativa. Desativar continua
  // possível pelo "Gerenciar produtos em lote".
  const brokerDetails = useBrokerDetails(brokerId);
  const manageBlockedReason = brokerActionBlockedReason(
    brokerDetails,
    'gerenciar produtos'
  );

  const [isManageOpen, setIsManageOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [sort, setSort] = useState<SortState>(null);

  const products = data?.data || [];
  const groupedByCategory = useMemo(() => {
    const base = groupProductsByCategory(products);
    if (!sort) return base;

    const direction = sort.direction === 'asc' ? 1 : -1;

    const sortedGroups = base
      .map(group => ({
        ...group,
        products: [...group.products].sort((a, b) => {
          return (
            direction *
            compareProducts(a, b, {
              column: sort.column,
            })
          );
        }),
      }))
      .sort((a, b) => {
        return (
          direction *
          compareGroups(a, b, {
            column: sort.column,
          })
        );
      });

    return sortedGroups;
  }, [products, sort]);

  const toggleSort = (column: SortColumn) => {
    setSort(prev => {
      if (!prev || prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return null;
    });
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-alba-gray-600'>Carregando produtos...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center justify-center py-12 gap-4'>
        <div className='text-alba-error-500'>
          {error?.message || 'Erro ao carregar produtos'}
        </div>
      </div>
    );
  }

  return (
    <div className='mt-6 flex flex-col gap-6 sm:gap-8'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-col gap-1'>
          <h2 className='text-lg font-normal text-alba-black-500'>
            Produtos da Corretora
          </h2>
          <p className='text-sm text-alba-gray-600'>
            Gerencie os produtos vinculados a esta corretora
          </p>
          {canManageProducts && manageBlockedReason && (
            <p className='mt-1 text-sm text-alba-error-red-500'>
              {manageBlockedReason}
            </p>
          )}
        </div>
        {canManageProducts && (
          <button
            type='button'
            title={manageBlockedReason}
            disabled={!brokerDetails.isActive}
            className='flex w-full items-center justify-center gap-2 rounded-xl bg-alba-blue-500 px-4 py-2 text-white transition-colors hover:bg-alba-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-alba-blue-500 sm:w-auto sm:px-8'
            onClick={() => setIsManageOpen(true)}
          >
            <Edit className='h-5 w-5 sm:h-6 sm:w-6' strokeWidth={2} />
            <span className='text-base font-bold'>Gerenciar Produtos</span>
          </button>
        )}
      </div>

      <div className='hidden sm:flex sm:flex-col bg-[#F9F9F9] rounded-3xl shadow-xl overflow-hidden'>
        <div className='grid grid-cols-4 items-center py-4 pl-14 pr-6 bg-alba-gray-200 gap-6'>
          <HeaderSortButton
            label='Produto'
            isActive={sort?.column === 'name'}
            direction={sort?.column === 'name' ? sort.direction : null}
            onClick={() => toggleSort('name')}
          />

          <HeaderSortButton
            label='Código'
            isActive={sort?.column === 'code'}
            direction={sort?.column === 'code' ? sort.direction : null}
            onClick={() => toggleSort('code')}
          />

          <HeaderSortButton
            label='Status'
            align='center'
            isActive={sort?.column === 'status'}
            direction={sort?.column === 'status' ? sort.direction : null}
            onClick={() => toggleSort('status')}
          />

          <div className='flex justify-end'>
            <span className='text-xs font-bold text-alba-black-500 uppercase'>
              Ações
            </span>
          </div>
        </div>

        <div className='flex flex-col'>
          {products.length === 0 ? (
            <div className='text-center py-12 text-alba-gray-600'>
              Nenhum produto vinculado
            </div>
          ) : (
            groupedByCategory.map((group, groupIndex) => {
              const isExpanded = Boolean(expandedCategories[group.key]);
              const isLastGroup = groupIndex === groupedByCategory.length - 1;

              return (
                <div key={group.key} className='flex flex-col'>
                  <CategoryRow
                    label={group.label}
                    productsCount={group.products.length}
                    status={group.status}
                    isExpanded={isExpanded}
                    isLast={!isExpanded && isLastGroup}
                    onToggle={() =>
                      setExpandedCategories(prev => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      }))
                    }
                  />

                  {isExpanded && (
                    <div className='flex flex-col px-6 py-1 gap-1'>
                      {group.products.map((product, productIndex) => (
                        <ProductRow
                          key={getProductKey(product)}
                          product={product}
                          isLast={
                            isLastGroup &&
                            productIndex === group.products.length - 1
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className='flex flex-col gap-3 sm:hidden'>
        {products.length === 0 ? (
          <div className='rounded-2xl border border-alba-gray-200 bg-white py-8 text-center text-alba-gray-600'>
            Nenhum produto vinculado
          </div>
        ) : (
          groupedByCategory.map(group => {
            const isExpanded = Boolean(expandedCategories[group.key]);
            return (
              <div
                key={group.key}
                className='rounded-2xl border border-alba-gray-200 bg-white px-4 py-3 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'
              >
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm font-bold text-alba-black-500'>
                      {group.label}
                    </p>
                    <p className='text-xs text-alba-gray-600'>
                      {group.products.length} produto(s)
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <StatusBadge status={group.status} />
                    <button
                      type='button'
                      onClick={() =>
                        setExpandedCategories(prev => ({
                          ...prev,
                          [group.key]: !prev[group.key],
                        }))
                      }
                      className='rounded p-1 hover:bg-alba-gray-200'
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <ChevronUp className='h-5 w-5 text-alba-black-500' />
                      ) : (
                        <ChevronDown className='h-5 w-5 text-alba-black-500' />
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className='mt-3 flex flex-col gap-2'>
                    {group.products.map(product => (
                      <div
                        key={getProductKey(product)}
                        className='rounded-xl border border-alba-gray-200 bg-[#F9F9F9] px-3 py-2'
                      >
                        <p className='text-sm font-medium text-alba-black-500'>
                          {product.name}
                        </p>
                        <div className='mt-2 flex items-center justify-between'>
                          <span className='text-xs text-alba-gray-600'>
                            Código: {product.productCode || product.code || '-'}
                          </span>
                          <StatusBadge status={product.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <BrokerProductsModal
        brokerId={brokerId}
        isOpen={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        products={products}
      />
    </div>
  );
}

interface ProductRowProps {
  product: BrokerProduct;
  isLast: boolean;
}

function ProductRow({ product }: ProductRowProps) {
  return (
    <div className='grid grid-cols-4 items-center py-2 pl-14 pr-6 gap-6'>
      <div className='flex items-center pl-4'>
        <span className='text-sm text-alba-black-500'>{product.name}</span>
      </div>

      <div className='flex items-center'>
        <span className='text-sm text-alba-gray-700'>
          {product.productCode || product.code || '-'}
        </span>
      </div>

      <div className='flex justify-center items-center'>
        <StatusBadge status={product.status} />
      </div>

      <div className='flex justify-end items-center'>
        <div className='w-6 h-6' />
      </div>
    </div>
  );
}

type CategoryGroup = {
  key: string;
  label: string;
  products: BrokerProduct[];
  status: 'active' | 'inactive';
};

function groupProductsByCategory(products: BrokerProduct[]): CategoryGroup[] {
  return groupProductsByGroup(products).map(group => ({
    key: group.key,
    label: group.label,
    products: group.products,
    status: (group.products.some(p => p.status === 'active')
      ? 'active'
      : 'inactive') as 'active' | 'inactive',
  }));
}

function getProductKey(product: BrokerProduct) {
  return product.id ?? product.productId ?? product.productCode ?? product.code;
}

type HeaderSortButtonProps = {
  label: string;
  align?: 'left' | 'center';
  isActive: boolean;
  direction: SortDirection | null;
  onClick: () => void;
};

function HeaderSortButton({
  label,
  align = 'left',
  isActive,
  direction,
  onClick,
}: HeaderSortButtonProps) {
  const Icon = isActive
    ? direction === 'asc'
      ? ChevronUp
      : ChevronDown
    : null;
  const alignClass = align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <button
      type='button'
      onClick={onClick}
      className={`flex items-center gap-2 ${alignClass} hover:bg-alba-gray-300/60 rounded-md px-2 py-1 transition-colors`}
      aria-label={`Ordenar por ${label}`}
    >
      <span className='text-xs font-bold text-alba-black-500 uppercase'>
        {label}
      </span>
      {Icon ? (
        <Icon className='h-4 w-4 text-alba-gray-500' />
      ) : (
        <ArrowDownUp className='h-4 w-4 text-alba-gray-500' />
      )}
    </button>
  );
}

function statusRank(status: 'active' | 'inactive') {
  return status === 'active' ? 0 : 1;
}

function compareGroups(
  a: CategoryGroup,
  b: CategoryGroup,
  opts: { column: SortColumn }
) {
  if (opts.column === 'code') {
    const diff = a.products.length - b.products.length;
    if (diff !== 0) return diff;
  }

  if (opts.column === 'status') {
    const diff = statusRank(a.status) - statusRank(b.status);
    if (diff !== 0) return diff;
  }

  // default/name tie-breaker
  return a.label.localeCompare(b.label);
}

function compareProducts(
  a: BrokerProduct,
  b: BrokerProduct,
  opts: { column: SortColumn }
) {
  if (opts.column === 'status') {
    const diff = statusRank(a.status) - statusRank(b.status);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name);
  }

  if (opts.column === 'code') {
    const aCode = a.productCode || a.code || '';
    const bCode = b.productCode || b.code || '';
    const diff = aCode.localeCompare(bCode);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name);
  }

  // name
  return a.name.localeCompare(b.name);
}

type CategoryRowProps = {
  label: string;
  productsCount: number;
  status: 'active' | 'inactive';
  isExpanded: boolean;
  isLast: boolean;
  onToggle: () => void;
};

function CategoryRow({
  label,
  productsCount,
  status,
  isExpanded,
  isLast,
  onToggle,
}: CategoryRowProps) {
  return (
    <div
      className={`grid grid-cols-4 items-center py-4 pl-14 pr-6 gap-6 ${
        !isLast && !isExpanded ? 'border-b border-alba-gray-300' : ''
      }`}
    >
      <div className='flex flex-col justify-center'>
        <span className='text-sm font-bold text-alba-black-500'>{label}</span>
      </div>

      <div className='flex items-center'>
        <span className='text-sm text-alba-gray-600'>{productsCount}</span>
      </div>

      <div className='flex justify-center items-center -ml-[10px]'>
        <StatusBadge status={status} />
      </div>

      <div className='flex justify-end items-center'>
        <button
          type='button'
          onClick={onToggle}
          className='p-1 hover:bg-alba-gray-200 rounded transition-colors'
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Recolher categoria' : 'Expandir categoria'}
        >
          {isExpanded ? (
            <ChevronUp
              className='h-6 w-6 text-alba-black-500'
              strokeWidth={2}
            />
          ) : (
            <ChevronDown
              className='h-6 w-6 text-alba-black-500'
              strokeWidth={2}
            />
          )}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'active' | 'inactive' }) {
  const isActive = status === 'active';

  return (
    <div
      className='px-2 py-0.5 rounded-full text-xs font-bold'
      style={{
        backgroundColor: isActive ? '#2ED47733' : '#F4636333',
        color: isActive ? '#2ED477' : '#F46363',
      }}
    >
      {isActive ? 'Ativo' : 'Inativo'}
    </div>
  );
}
